import Phaser from 'phaser';
import { World } from '../ecs/World';
import { InventoryComponent } from '../ecs/components/InventoryComponent';
import { HealthComponent } from '../ecs/components/HealthComponent';
import { BeastieComponent } from '../ecs/components/BeastieComponent';
import { ItemRegistry, ItemDefinition } from '../data/ItemRegistry';
import { EventBus } from '../utils/EventBus';

/**
 * UI state for an inventory slot
 */
interface SlotUI {
  container: Phaser.GameObjects.Container;
  background: Phaser.GameObjects.Graphics;
  icon: Phaser.GameObjects.Graphics;
  quantityText: Phaser.GameObjects.Text;
  hitArea: Phaser.GameObjects.Rectangle;
}

/**
 * UI state for a target selection button
 */
interface TargetUI {
  entityId: string;
  container: Phaser.GameObjects.Container;
  hitArea: Phaser.GameObjects.Rectangle;
}

/**
 * InventoryPanel - Modal panel for viewing and using items
 */
export class InventoryPanel {
  private scene: Phaser.Scene;
  private world: World;
  private container!: Phaser.GameObjects.Container;
  private background!: Phaser.GameObjects.Graphics;

  // Slot grid
  private slots: SlotUI[] = [];
  private selectedSlotIndex: number = -1;

  // Target selection
  private targetSelectionMode: boolean = false;
  private selectedItemId: string | null = null;
  private targetButtons: TargetUI[] = [];
  private targetContainer!: Phaser.GameObjects.Container;

  // UI elements
  private titleText!: Phaser.GameObjects.Text;
  private descriptionText!: Phaser.GameObjects.Text;
  private useButton!: Phaser.GameObjects.Container;
  private useButtonHitArea!: Phaser.GameObjects.Rectangle;
  private closeButtonHitArea!: Phaser.GameObjects.Rectangle;

  // Layout - compact side panel
  private panelWidth = 200;
  private panelHeight = 300;
  private gridCols = 3;
  private gridRows = 4;
  private slotSize = 40;
  private slotPadding = 6;
  private panelX = 0;
  private panelY = 0;

  private isVisible = false;

  // Keyboard navigation state
  private selectedTargetIndex: number = 0;
  private validTargets: string[] = [];

  constructor(scene: Phaser.Scene, world: World) {
    this.scene = scene;
    this.world = world;
    this.createPanel();
    this.setupEventListeners();

    // Ensure panel starts hidden (set isVisible true so hide() doesn't early-return)
    this.isVisible = true;
    this.hide();
  }

  private createPanel(): void {
    const { width, height } = this.scene.cameras.main;

    // Position on right side of screen
    this.panelX = width - this.panelWidth / 2 - 10;
    this.panelY = height / 2;

    this.container = this.scene.add.container(this.panelX, this.panelY);
    this.container.setScrollFactor(0);
    this.container.setDepth(1500);

    // Panel background (no full-screen overlay)
    this.background = this.scene.add.graphics();
    this.background.fillStyle(0x1a1a2e, 0.92);
    this.background.fillRoundedRect(
      -this.panelWidth / 2,
      -this.panelHeight / 2,
      this.panelWidth,
      this.panelHeight,
      8
    );
    this.background.lineStyle(2, 0x444466, 1);
    this.background.strokeRoundedRect(
      -this.panelWidth / 2,
      -this.panelHeight / 2,
      this.panelWidth,
      this.panelHeight,
      8
    );
    this.container.add(this.background);

    // Title
    this.titleText = this.scene.add
      .text(0, -this.panelHeight / 2 + 16, 'INVENTORY', {
        fontFamily: 'monospace',
        fontSize: '14px',
        color: '#ffffff',
        fontStyle: 'bold',
      })
      .setOrigin(0.5);
    this.container.add(this.titleText);

    // Close button (smaller)
    const closeX = this.panelWidth / 2 - 18;
    const closeY = -this.panelHeight / 2 + 16;
    const closeBtn = this.scene.add.graphics();
    closeBtn.fillStyle(0x663333, 1);
    closeBtn.fillRoundedRect(closeX - 10, closeY - 10, 20, 20, 4);
    closeBtn.lineStyle(1, 0x884444, 1);
    closeBtn.strokeRoundedRect(closeX - 10, closeY - 10, 20, 20, 4);
    this.container.add(closeBtn);

    const closeText = this.scene.add.text(closeX, closeY, 'X', {
      fontFamily: 'monospace',
      fontSize: '12px',
      color: '#ff6666',
      fontStyle: 'bold',
    }).setOrigin(0.5);
    this.container.add(closeText);

    this.closeButtonHitArea = this.scene.add.rectangle(
      this.panelX + closeX,
      this.panelY + closeY,
      20, 20, 0x000000, 0
    );
    this.closeButtonHitArea.setScrollFactor(0);
    this.closeButtonHitArea.setDepth(1501);
    this.closeButtonHitArea.setInteractive({ useHandCursor: true });
    this.closeButtonHitArea.on('pointerdown', () => this.hide());

    // Create slot grid
    this.createSlotGrid();

    // Description text
    this.descriptionText = this.scene.add
      .text(0, this.panelHeight / 2 - 70, '', {
        fontFamily: 'monospace',
        fontSize: '10px',
        color: '#aaaaaa',
        wordWrap: { width: this.panelWidth - 20 },
        align: 'center',
      })
      .setOrigin(0.5);
    this.container.add(this.descriptionText);

    // Use button
    this.createUseButton();

    // Target selection container (hidden initially)
    this.targetContainer = this.scene.add.container(0, 0);
    this.targetContainer.setVisible(false);
    this.container.add(this.targetContainer);

    // Keyboard controls - use scene-level keydown events for reliability
    this.scene.input.keyboard?.on('keydown', (event: KeyboardEvent) => {
      if (!this.isVisible) return;

      switch (event.code) {
        case 'Escape':
          if (this.targetSelectionMode) {
            this.cancelTargetSelection();
          } else {
            this.hide();
          }
          event.preventDefault();
          break;

        case 'ArrowUp':
        case 'KeyW':
          this.handleArrowKey('up');
          event.preventDefault();
          break;

        case 'ArrowDown':
        case 'KeyS':
          this.handleArrowKey('down');
          event.preventDefault();
          break;

        case 'ArrowLeft':
        case 'KeyA':
          this.handleArrowKey('left');
          event.preventDefault();
          break;

        case 'ArrowRight':
        case 'KeyD':
          this.handleArrowKey('right');
          event.preventDefault();
          break;

        case 'Enter':
        case 'Space':
          this.handleEnterKey();
          event.preventDefault();
          break;

        case 'Digit1':
        case 'Numpad1':
          this.handleNumberKey(0);
          break;
        case 'Digit2':
        case 'Numpad2':
          this.handleNumberKey(1);
          break;
        case 'Digit3':
        case 'Numpad3':
          this.handleNumberKey(2);
          break;
        case 'Digit4':
        case 'Numpad4':
          this.handleNumberKey(3);
          break;
        case 'Digit5':
        case 'Numpad5':
          this.handleNumberKey(4);
          break;
        case 'Digit6':
        case 'Numpad6':
          this.handleNumberKey(5);
          break;
        case 'Digit7':
        case 'Numpad7':
          this.handleNumberKey(6);
          break;
        case 'Digit8':
        case 'Numpad8':
          this.handleNumberKey(7);
          break;
        case 'Digit9':
        case 'Numpad9':
          this.handleNumberKey(8);
          break;
        case 'Digit0':
        case 'Numpad0':
          this.handleNumberKey(9);
          break;
      }
    });
  }

  /**
   * Handle arrow key navigation
   */
  private handleArrowKey(direction: 'up' | 'down' | 'left' | 'right'): void {
    if (!this.isVisible) return;

    if (this.targetSelectionMode) {
      // Navigate targets with up/down
      if (direction === 'up' && this.selectedTargetIndex > 0) {
        this.selectedTargetIndex--;
        this.updateTargetHighlight();
      } else if (direction === 'down' && this.selectedTargetIndex < this.validTargets.length - 1) {
        this.selectedTargetIndex++;
        this.updateTargetHighlight();
      }
    } else {
      // Navigate inventory grid
      const cols = this.gridCols;
      let newIndex = this.selectedSlotIndex;

      // If nothing selected, start at 0
      if (newIndex < 0) newIndex = 0;

      switch (direction) {
        case 'up':
          newIndex = Math.max(0, newIndex - cols);
          break;
        case 'down':
          newIndex = Math.min(this.slots.length - 1, newIndex + cols);
          break;
        case 'left':
          newIndex = Math.max(0, newIndex - 1);
          break;
        case 'right':
          newIndex = Math.min(this.slots.length - 1, newIndex + 1);
          break;
      }

      this.selectedSlotIndex = newIndex;
      this.refresh();
    }
  }

  /**
   * Handle Enter key - use item or confirm target
   */
  private handleEnterKey(): void {
    if (!this.isVisible) return;

    if (this.targetSelectionMode) {
      // Confirm target selection
      if (this.validTargets.length > 0 && this.selectedTargetIndex < this.validTargets.length) {
        this.selectTarget(this.validTargets[this.selectedTargetIndex]);
      }
    } else {
      // Use selected item
      this.useSelectedItem();
    }
  }

  /**
   * Handle number key quick selection
   */
  private handleNumberKey(slotIndex: number): void {
    if (!this.isVisible || this.targetSelectionMode) return;

    // Check if slot has an item
    const player = this.world.getEntitiesByTag('player')[0];
    const inventory = player?.getComponent<InventoryComponent>('inventory');

    if (inventory?.slots[slotIndex]) {
      this.selectedSlotIndex = slotIndex;
      this.refresh();
    }
  }

  /**
   * Update target highlight during keyboard navigation
   */
  private updateTargetHighlight(): void {
    const buttonWidth = this.panelWidth - 20;

    this.targetButtons.forEach((target, index) => {
      const isSelected = index === this.selectedTargetIndex;
      const bg = target.container.getAt(0) as Phaser.GameObjects.Graphics;

      bg.clear();
      if (isSelected) {
        bg.fillStyle(0x555588, 1);
        bg.fillRoundedRect(-buttonWidth / 2, -14, buttonWidth, 28, 4);
        bg.lineStyle(2, 0x8888ff, 1);
        bg.strokeRoundedRect(-buttonWidth / 2, -14, buttonWidth, 28, 4);
      } else {
        bg.fillStyle(0x333355, 1);
        bg.fillRoundedRect(-buttonWidth / 2, -14, buttonWidth, 28, 4);
        bg.lineStyle(1, 0x555577, 1);
        bg.strokeRoundedRect(-buttonWidth / 2, -14, buttonWidth, 28, 4);
      }
    });
  }

  private createSlotGrid(): void {
    const startX = -((this.gridCols - 1) * (this.slotSize + this.slotPadding)) / 2;
    const startY = -30;

    for (let row = 0; row < this.gridRows; row++) {
      for (let col = 0; col < this.gridCols; col++) {
        const x = startX + col * (this.slotSize + this.slotPadding);
        const y = startY + row * (this.slotSize + this.slotPadding);
        const slotIndex = row * this.gridCols + col;

        const slotContainer = this.scene.add.container(x, y);

        // Slot background
        const bg = this.scene.add.graphics();
        bg.fillStyle(0x2a2a4e, 1);
        bg.fillRoundedRect(-this.slotSize / 2, -this.slotSize / 2, this.slotSize, this.slotSize, 4);
        bg.lineStyle(1, 0x444466, 1);
        bg.strokeRoundedRect(-this.slotSize / 2, -this.slotSize / 2, this.slotSize, this.slotSize, 4);
        slotContainer.add(bg);

        // Item icon placeholder
        const icon = this.scene.add.graphics();
        slotContainer.add(icon);

        // Quantity text
        const quantityText = this.scene.add
          .text(this.slotSize / 2 - 6, this.slotSize / 2 - 6, '', {
            fontFamily: 'monospace',
            fontSize: '10px',
            color: '#ffffff',
            stroke: '#000000',
            strokeThickness: 2,
          })
          .setOrigin(1);
        slotContainer.add(quantityText);

        this.container.add(slotContainer);

        // Hit area for slot
        const hitArea = this.scene.add.rectangle(
          this.panelX + x,
          this.panelY + y,
          this.slotSize,
          this.slotSize,
          0x000000,
          0
        );
        hitArea.setScrollFactor(0);
        hitArea.setDepth(1501);
        hitArea.setInteractive({ useHandCursor: true });
        hitArea.on('pointerdown', () => this.selectSlot(slotIndex));
        hitArea.on('pointerover', () => this.hoverSlot(slotIndex, bg));
        hitArea.on('pointerout', () => this.unhoverSlot(slotIndex, bg));

        this.slots.push({
          container: slotContainer,
          background: bg,
          icon,
          quantityText,
          hitArea,
        });
      }
    }
  }

  private createUseButton(): void {
    const buttonY = this.panelHeight / 2 - 30;

    this.useButton = this.scene.add.container(0, buttonY);

    const bg = this.scene.add.graphics();
    bg.fillStyle(0x336633, 1);
    bg.fillRoundedRect(-40, -12, 80, 24, 4);
    bg.lineStyle(1, 0x448844, 1);
    bg.strokeRoundedRect(-40, -12, 80, 24, 4);
    this.useButton.add(bg);

    const text = this.scene.add.text(0, 0, 'USE', {
      fontFamily: 'monospace',
      fontSize: '12px',
      color: '#ffffff',
      fontStyle: 'bold',
    }).setOrigin(0.5);
    this.useButton.add(text);

    this.useButton.setVisible(false);
    this.container.add(this.useButton);

    // Hit area
    this.useButtonHitArea = this.scene.add.rectangle(
      this.panelX,
      this.panelY + buttonY,
      80,
      24,
      0x000000,
      0
    );
    this.useButtonHitArea.setScrollFactor(0);
    this.useButtonHitArea.setDepth(1501);
    this.useButtonHitArea.setInteractive({ useHandCursor: true });
    this.useButtonHitArea.on('pointerdown', () => this.useSelectedItem());
    this.useButtonHitArea.setVisible(false);
  }

  private setupEventListeners(): void {
    EventBus.onEvent('ui:show_inventory', () => this.show());
    EventBus.onEvent('ui:hide_inventory', () => this.hide());
    EventBus.onEvent('inventory:changed', () => {
      if (this.isVisible) this.refresh();
    });
    EventBus.onEvent('inventory:item_used', () => {
      this.cancelTargetSelection();
      this.selectedSlotIndex = -1;
      this.refresh();
    });
  }

  /**
   * Show the inventory panel
   */
  public show(): void {
    if (this.isVisible) return;

    this.isVisible = true;
    this.container.setVisible(true);
    this.closeButtonHitArea.setVisible(true);

    // Show slot hit areas
    for (const slot of this.slots) {
      slot.hitArea.setVisible(true);
      slot.hitArea.setInteractive({ useHandCursor: true });
    }

    // Auto-select first item if nothing selected
    if (this.selectedSlotIndex < 0) {
      const player = this.world.getEntitiesByTag('player')[0];
      const inventory = player?.getComponent<InventoryComponent>('inventory');
      if (inventory && inventory.slots.length > 0) {
        this.selectedSlotIndex = 0;
      }
    }

    this.refresh();
    EventBus.emitEvent('ui:menuOpened', { menu: 'inventory' });
  }

  /**
   * Hide the inventory panel
   */
  public hide(): void {
    if (!this.isVisible) return;

    this.isVisible = false;
    this.container.setVisible(false);
    this.closeButtonHitArea.setVisible(false);
    this.useButtonHitArea.setVisible(false);

    // Hide slot hit areas
    for (const slot of this.slots) {
      slot.hitArea.setVisible(false);
      slot.hitArea.disableInteractive();
    }

    this.cancelTargetSelection();
    this.selectedSlotIndex = -1;

    EventBus.emitEvent('ui:menuClosed', { menu: 'inventory' });
  }

  /**
   * Toggle visibility
   */
  public toggle(): void {
    if (this.isVisible) {
      this.hide();
    } else {
      this.show();
    }
  }

  /**
   * Refresh inventory display
   */
  public refresh(): void {
    const player = this.world.getEntitiesByTag('player')[0];
    if (!player) return;

    const inventory = player.getComponent<InventoryComponent>('inventory');
    if (!inventory) return;

    // Update each slot
    for (let i = 0; i < this.slots.length; i++) {
      const slot = this.slots[i];
      const inventorySlot = inventory.slots[i];

      slot.icon.clear();
      slot.quantityText.setText('');

      if (inventorySlot) {
        this.drawItemIcon(slot.icon, inventorySlot.itemId);
        if (inventorySlot.quantity > 1) {
          slot.quantityText.setText(String(inventorySlot.quantity));
        }
      }

      // Update selection highlight
      this.updateSlotHighlight(i);
    }

    // Update description and use button
    this.updateSelectedItemUI();
  }

  private drawItemIcon(graphics: Phaser.GameObjects.Graphics, itemId: string): void {
    const item = ItemRegistry.get(itemId);
    if (!item) return;

    // Simple colored square based on item type
    let color = 0x888888;
    if (item.effect?.type === 'revive') {
      color = 0x44ff88; // Green for revive
    } else if (item.effect?.type === 'heal') {
      color = 0xff4488; // Pink for heal
    } else if (item.type === 'consumable') {
      color = 0xffaa44; // Orange for other consumables
    }

    graphics.fillStyle(color, 1);
    graphics.fillRoundedRect(-14, -14, 28, 28, 4);
    graphics.lineStyle(1, 0xffffff, 0.3);
    graphics.strokeRoundedRect(-14, -14, 28, 28, 4);
  }

  private selectSlot(index: number): void {
    if (this.targetSelectionMode) return;

    const player = this.world.getEntitiesByTag('player')[0];
    const inventory = player?.getComponent<InventoryComponent>('inventory');
    if (!inventory || !inventory.slots[index]) {
      this.selectedSlotIndex = -1;
    } else {
      this.selectedSlotIndex = index;
    }

    this.refresh();
  }

  private hoverSlot(index: number, bg: Phaser.GameObjects.Graphics): void {
    if (this.targetSelectionMode) return;

    const player = this.world.getEntitiesByTag('player')[0];
    const inventory = player?.getComponent<InventoryComponent>('inventory');
    if (inventory?.slots[index]) {
      bg.clear();
      bg.fillStyle(0x3a3a6e, 1);
      bg.fillRoundedRect(-this.slotSize / 2, -this.slotSize / 2, this.slotSize, this.slotSize, 4);
      bg.lineStyle(1, 0x6666aa, 1);
      bg.strokeRoundedRect(-this.slotSize / 2, -this.slotSize / 2, this.slotSize, this.slotSize, 4);
    }
  }

  private unhoverSlot(index: number, _bg: Phaser.GameObjects.Graphics): void {
    this.updateSlotHighlight(index);
  }

  private updateSlotHighlight(index: number): void {
    const slot = this.slots[index];
    const isSelected = index === this.selectedSlotIndex;

    slot.background.clear();
    if (isSelected) {
      slot.background.fillStyle(0x4a4a7e, 1);
      slot.background.fillRoundedRect(-this.slotSize / 2, -this.slotSize / 2, this.slotSize, this.slotSize, 4);
      slot.background.lineStyle(2, 0x88aaff, 1);
      slot.background.strokeRoundedRect(-this.slotSize / 2, -this.slotSize / 2, this.slotSize, this.slotSize, 4);
    } else {
      slot.background.fillStyle(0x2a2a4e, 1);
      slot.background.fillRoundedRect(-this.slotSize / 2, -this.slotSize / 2, this.slotSize, this.slotSize, 4);
      slot.background.lineStyle(1, 0x444466, 1);
      slot.background.strokeRoundedRect(-this.slotSize / 2, -this.slotSize / 2, this.slotSize, this.slotSize, 4);
    }
  }

  private updateSelectedItemUI(): void {
    if (this.selectedSlotIndex < 0) {
      this.descriptionText.setText('Arrow keys to navigate, Enter to use\n1-9 for quick select');
      this.useButton.setVisible(false);
      this.useButtonHitArea.setVisible(false);
      return;
    }

    const player = this.world.getEntitiesByTag('player')[0];
    const inventory = player?.getComponent<InventoryComponent>('inventory');
    const inventorySlot = inventory?.slots[this.selectedSlotIndex];

    if (!inventorySlot) {
      this.descriptionText.setText('Select an item');
      this.useButton.setVisible(false);
      this.useButtonHitArea.setVisible(false);
      return;
    }

    const item = ItemRegistry.get(inventorySlot.itemId);
    if (!item) return;

    this.descriptionText.setText(`${item.name}\n${item.description}`);

    // Show use button for consumables
    if (item.type === 'consumable' && item.effect) {
      this.useButton.setVisible(true);
      this.useButtonHitArea.setVisible(true);
    } else {
      this.useButton.setVisible(false);
      this.useButtonHitArea.setVisible(false);
    }
  }

  private useSelectedItem(): void {
    if (this.selectedSlotIndex < 0) return;

    const player = this.world.getEntitiesByTag('player')[0];
    const inventory = player?.getComponent<InventoryComponent>('inventory');
    const inventorySlot = inventory?.slots[this.selectedSlotIndex];

    if (!inventorySlot) return;

    const item = ItemRegistry.get(inventorySlot.itemId);
    if (!item?.effect) return;

    // If item requires target, show target selection
    if (item.effect.requiresTarget) {
      this.showTargetSelection(inventorySlot.itemId, item);
    } else {
      // Use item without target
      EventBus.emitEvent('inventory:use_item', { itemId: inventorySlot.itemId });
    }
  }

  private showTargetSelection(itemId: string, item: ItemDefinition): void {
    this.targetSelectionMode = true;
    this.selectedItemId = itemId;
    this.selectedTargetIndex = 0;

    // Update title
    this.titleText.setText('SELECT TARGET (Up/Down, Enter)');

    // Hide slot interactions
    for (const slot of this.slots) {
      slot.hitArea.disableInteractive();
    }
    this.useButton.setVisible(false);
    this.useButtonHitArea.setVisible(false);

    // Clear previous targets
    this.clearTargetButtons();
    this.validTargets = [];

    // Get valid targets based on filter
    const partyMembers = this.world.getEntitiesByTag('party_member');
    const filteredTargets = partyMembers.filter((entity) => {
      const health = entity.getComponent<HealthComponent>('health');
      if (!health) return false;

      if (item.effect?.targetFilter === 'fainted') {
        return health.isFainted;
      } else if (item.effect?.targetFilter === 'damaged') {
        return !health.isFainted && health.isAlive && health.healthPercent < 1;
      }
      return true;
    });

    // Store valid target IDs for keyboard navigation
    this.validTargets = filteredTargets.map((e) => e.id);

    // Create target buttons
    const startY = -60;
    filteredTargets.forEach((entity, index) => {
      this.createTargetButton(entity.id, startY + index * 50);
    });

    if (filteredTargets.length === 0) {
      const noTargetsText = this.scene.add.text(0, 0, 'No valid targets', {
        fontFamily: 'monospace',
        fontSize: '14px',
        color: '#ff6666',
      }).setOrigin(0.5);
      this.targetContainer.add(noTargetsText);
    } else {
      // Highlight first target for keyboard navigation
      this.updateTargetHighlight();
    }

    this.targetContainer.setVisible(true);
  }

  private createTargetButton(entityId: string, y: number): void {
    const entity = this.world.getEntity(entityId);
    if (!entity) return;

    const beastie = entity.getComponent<BeastieComponent>('beastie');
    const health = entity.getComponent<HealthComponent>('health');
    if (!beastie || !health) return;

    const buttonWidth = this.panelWidth - 20;
    const buttonContainer = this.scene.add.container(0, y);

    // Button background
    const bg = this.scene.add.graphics();
    bg.fillStyle(0x333355, 1);
    bg.fillRoundedRect(-buttonWidth / 2, -14, buttonWidth, 28, 4);
    bg.lineStyle(1, 0x555577, 1);
    bg.strokeRoundedRect(-buttonWidth / 2, -14, buttonWidth, 28, 4);
    buttonContainer.add(bg);

    // Beastie name
    const nameText = this.scene.add.text(-buttonWidth / 2 + 8, -5, beastie.nickname || beastie.speciesId, {
      fontFamily: 'monospace',
      fontSize: '10px',
      color: '#ffffff',
    });
    buttonContainer.add(nameText);

    // HP info
    const hpText = this.scene.add.text(buttonWidth / 2 - 8, -5, `${health.current}/${health.max}`, {
      fontFamily: 'monospace',
      fontSize: '9px',
      color: health.isFainted ? '#ff6666' : '#66ff66',
    }).setOrigin(1, 0);
    buttonContainer.add(hpText);

    this.targetContainer.add(buttonContainer);

    // Hit area
    const hitArea = this.scene.add.rectangle(
      this.panelX,
      this.panelY + y,
      buttonWidth,
      28,
      0x000000,
      0
    );
    hitArea.setScrollFactor(0);
    hitArea.setDepth(1502);
    hitArea.setInteractive({ useHandCursor: true });
    hitArea.on('pointerdown', () => this.selectTarget(entityId));
    hitArea.on('pointerover', () => {
      bg.clear();
      bg.fillStyle(0x444466, 1);
      bg.fillRoundedRect(-buttonWidth / 2, -14, buttonWidth, 28, 4);
      bg.lineStyle(1, 0x6666aa, 1);
      bg.strokeRoundedRect(-buttonWidth / 2, -14, buttonWidth, 28, 4);
    });
    hitArea.on('pointerout', () => {
      bg.clear();
      bg.fillStyle(0x333355, 1);
      bg.fillRoundedRect(-buttonWidth / 2, -14, buttonWidth, 28, 4);
      bg.lineStyle(1, 0x555577, 1);
      bg.strokeRoundedRect(-buttonWidth / 2, -14, buttonWidth, 28, 4);
    });

    this.targetButtons.push({ entityId, container: buttonContainer, hitArea });
  }

  private selectTarget(entityId: string): void {
    if (!this.selectedItemId) return;

    EventBus.emitEvent('inventory:use_item', {
      itemId: this.selectedItemId,
      targetEntityId: entityId,
    });
  }

  private cancelTargetSelection(): void {
    if (!this.targetSelectionMode) return;

    this.targetSelectionMode = false;
    this.selectedItemId = null;
    this.titleText.setText('INVENTORY');

    // Clear target buttons
    this.clearTargetButtons();
    this.targetContainer.setVisible(false);

    // Re-enable slot interactions
    for (const slot of this.slots) {
      slot.hitArea.setInteractive({ useHandCursor: true });
    }

    this.updateSelectedItemUI();
  }

  private clearTargetButtons(): void {
    for (const target of this.targetButtons) {
      target.container.destroy(true);
      target.hitArea.destroy();
    }
    this.targetButtons = [];
    this.targetContainer.removeAll(true);
  }

  public getIsVisible(): boolean {
    return this.isVisible;
  }

  public destroy(): void {
    // Clean up hit areas
    this.closeButtonHitArea.destroy();
    this.useButtonHitArea.destroy();
    for (const slot of this.slots) {
      slot.hitArea.destroy();
    }
    this.clearTargetButtons();

    this.container.destroy(true);
    this.slots = [];
  }
}
